import { ChangeDetectorRef, Component, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of, takeUntil, Subject, Observable, filter } from 'rxjs';
import { DateTime } from 'luxon';

import { Category } from '@api/models/category';
import { EngagementRequest } from '@api/models/engagement-request';
import { ActiveReportEnum } from '@api/models/active-report-enum';
import { IdName } from '@api/models/idname';
import { PaginatorParams } from '@api/models/paginator';
import { StatisticsFilterInputs } from '@app/models/statistics-filter-inputs';
import { ClientApi } from '@api/services/client-api';
import { Channel, Customer, CustomerApi, Engagement, EngagementTypeEnum, ReportsResponse, StatisticsApi, TimeframeEnum } from '@api/index';
import { MonthTranslations } from '@app/core/translations';
import { AuthService } from '@core/auth/auth.service';
import { UserRoleEnum } from '@app/admin/user/model/user.model';
import { EnumService } from '@core/services/enum.service';
import { TranslateConfigService } from '@core/translate-config.service';
import { StatisticsService } from '../statistics.service';
import { BookingPageExposureDetails } from '@app/admin/engagement/bookings/model/bookings.model';
import { DeviceType } from '@app/shared/models/enums/deviceType.enum';
import filterConfig from './reports-filter-config';
import reportsFormConfig from './reports-form-config';
import { EngagementResponse } from '@api/models/engagement';
import { ReachReportTypeEnum } from '../reach/model/reach-report-type-enum';
import { getLastCalendarYear, getTimeFrameFunctionMap } from '@app/shared/utils/timeframe.utils';
import { apiCallCatchError } from '@app/shared/utils/utils';
import { ActiveScreenOrGroupParameters } from '@admin/screen/model/screen-group.model';
import { ScreenOrGroupFilterService } from '../screen-or-group-filter.service';

@Component({
  selector: 'flow-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  providers: [ScreenOrGroupFilterService],
})
export class ReportsComponent implements OnInit, OnDestroy {
  reportType: ActiveReportEnum;
  permissions?: string[];

  isAdmin: boolean;
  customerId: number;
  placements: IdName[] = [];
  engagements: EngagementResponse;
  medias: IdName[];
  filteredEngagements: Engagement[];
  filteredMedias: IdName[];
  reports: ReportsResponse;
  isDownloadEnabled: boolean = false;
  activeReport: ActiveReportEnum = ActiveReportEnum.PeopleCounter;
  ActiveReportEnum = ActiveReportEnum;
  title: string = 'reports.titlePeopleCounter';
  info: string = 'reports.infoPeopleCounter';
  startDate: any;
  endDate: any;
  preselectedStartDate: any;
  preselectedEndDate: any;
  filtersData = {
    channelIds: [],
    screenIds: [],
    placement: [],
    bookingNumbers: [],
    customerId: [],
  };
  preSelectedFilters = false;
  categories: Category[];
  pois: IdName[];
  filteredPOIs: IdName[];
  includeAdminDetailsPOI: boolean;
  includeSmsDetailsPOI: boolean;
  includeVehicleDataReach: boolean;
  includeAvgTimeInChannel: boolean;
  showDetailedReport: boolean;
  isFilterDataReady: boolean = false;
  filterInputs = new Map<string, StatisticsFilterInputs>();
  timeframeTypes = [];
  trackingTypes = [];
  engagementTypes = [];
  clients: IdName[] = [];
  nonAdmincustomerIds: number[];
  params: PaginatorParams = {
    pageNumber: 0,
    pageSize: 10,
    sortBy: 'Date',
    sortDirection: 'asc',
  };
  destroy$ = new Subject<void>();
  customers: Customer[] = [];
  finalEngagements: any = { items: [] };
  channelResponse: IdName[];
  ReachReportTypeEnum = ReachReportTypeEnum;
  peopleReport: ReportsResponse;
  vehicleReport: ReportsResponse;
  selectedIndex: number = 0;
  private lastUsedFilters: any;
  vehicleReportCount: string;
  peopleReportCount: string;
  activeChannels: Channel[];
  private lastScreenSearch = '';

  constructor(
    private authService: AuthService,
    private customerApi: CustomerApi,
    private formBuilder: FormBuilder,
    private enumService: EnumService,
    private cdr: ChangeDetectorRef,
    private statisticsApi: StatisticsApi,
    private translateConfigService: TranslateConfigService,
    private activatedRoute: ActivatedRoute,
    public statisticsService: StatisticsService,
    private clientApi: ClientApi,
    private screenFilter: ScreenOrGroupFilterService
  ) {
    this.statisticsService.form = this.formBuilder.group({});
  }

  ngOnInit(): void {
    this.activatedRoute.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.reportType = data?.reportType;
      this.permissions = data?.actionPermissions;
    });

    if (this.activatedRoute.snapshot.routeConfig.path == 'statistics/exposures-reports' || this.reportType === ActiveReportEnum.Exposures) {
      this.activeReport = ActiveReportEnum.Exposures;
      this.title = 'reports.titleExposures';
      this.info = 'reports.infoExposures';
    } else if (this.activatedRoute.snapshot.routeConfig.path === 'statistics/reach-reports' || this.reportType === ActiveReportEnum.Reach) {
      this.title = 'reports.titleReachReports';
      this.info = 'reports.infoReach';
      this.activeReport = ActiveReportEnum.Reach;
    } else if (this.activatedRoute.snapshot.routeConfig.path === 'statistics/poi-reports' || this.reportType === ActiveReportEnum.POI) {
      this.title = 'reports.titlePOI';
      this.activeReport = ActiveReportEnum.POI;
      this.info = 'reports.infoPOI';
    }
    this.timeframeTypes = this.enumService.timeframeTypes;
    this.trackingTypes = this.enumService.trackingTypes;
    this.engagementTypes = this.enumService.engagementTypes;

    const user = this.authService.userData;
    this.customerId = user?.currentCustomerId || user?.customerId;
    this.isAdmin = user.roleId === UserRoleEnum.Admin && !user.currentCustomerId;
    this.initForm();
    this.getActivatedRoute();
  }

  getActivatedRoute(): void {
    this.activatedRoute.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
      const preselectedEngagements: BookingPageExposureDetails = data?.engagement;
      this.getData(preselectedEngagements);
    });
  }

  getData(preselectedEngagements?: BookingPageExposureDetails): void {
    const isExposuresReport = this.activeReport === ActiveReportEnum.Exposures;
    const isReachOrExposures = [ActiveReportEnum.Reach, ActiveReportEnum.Exposures].includes(this.activeReport);

    const customers$ = this.isAdmin
      ? apiCallCatchError(this.customerApi.getCustomerOptions())
      : apiCallCatchError(this.customerApi.getCustomerById(this.customerId));

    const clients$ = isExposuresReport
      ? apiCallCatchError(this.clientApi.getClientOptions(this.isAdmin ? undefined : [this.customerId]))
      : of(null);

    forkJoin([customers$, clients$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([response, clients]) => {
        const customers = Array.isArray(response) ? response : [response];
        this.customers = customers;
        this.clients = clients;
        this.isFilterDataReady = true;

        if (!this.isAdmin) {
          this.setNonAdminCustomer(customers);
        }

        if (preselectedEngagements) {
          this.preselectedEngagements(preselectedEngagements, customers);
          return;
        }

        if (isExposuresReport && this.isAdmin) {
          this.getEngagements();
        }

        this.fillInputData('customerIds', customers);

        if (this.customerId) {
          this.fillInputData('clientIds', clients);
        }

        if (!this.isAdmin) {
          if (isReachOrExposures) {
            this.handleChannels(customers[0].channels, true);
          } else {
            this.handleChannels(customers[0].channels, false);
          }

          if (this.activeReport === ActiveReportEnum.POI) {
            this.getCategories([this.customerId]);
          }
        }
      });
  }

  private setNonAdminCustomer(customers: Customer[]): void {
    this.nonAdmincustomerIds = customers.map(c => c.id);
    this.statisticsService.form.get('customerIds').setValue(this.nonAdmincustomerIds);
    this.statisticsService.form.get('customerIds').disable({ onlySelf: true });
  }

  private handleChannels(channels: Channel[], onlyActive: boolean): void {
    const filteredChannels = onlyActive ? channels.filter(c => c.isActive || c.id === -1) : channels;

    this.activeChannels = filteredChannels;
    this.fillInputData('channelIds', filteredChannels);

    if (onlyActive) {
      const ids = filteredChannels.map(c => c.id);
      this.statisticsService.form.get('channelIds').setValue(ids);
      this.getPlacements();
    }
  }

  toggleChannelsForNonAdminRole(channelIds: number[]): void {
    if (channelIds.includes(-1)) {
      this.fillInputData('channelIds', this.customers[0].channels);
    } else {
      this.fillInputData('channelIds', this.activeChannels);
    }
  }

  preselectedEngagements(preselectedEngagements: BookingPageExposureDetails, customers: Customer[]): void {
    this.preSelectedFilters = true;
    const uniqueScreens = preselectedEngagements.screens;
    const uniqueChannels = preselectedEngagements.channels;
    const engagementsArray = [
      {
        id: preselectedEngagements.booking?.id,
        name: preselectedEngagements.booking.name,
      }
    ];

    if (preselectedEngagements.booking?.childPlaylistId) {
      engagementsArray.push({
        id: preselectedEngagements.booking?.childPlaylistId,
        name: preselectedEngagements.booking?.childPlaylistName,
      });
    }

    this.preselectedStartDate = preselectedEngagements?.startDate;
    this.preselectedEndDate = preselectedEngagements?.endDate;
    this.placements = preselectedEngagements?.placements.map(placement => {
      return {
        id: placement.id,
        name: placement.name,
      };
    });

    this.fillInputData('customerIds', customers);
    this.fillInputData('channelIds', uniqueChannels);
    this.fillInputData('screenIds', uniqueScreens);
    this.fillInputData('placement', this.placements);
    this.fillInputData('bookingNumbers', engagementsArray);

    this.filtersData['customerId'] = [preselectedEngagements.customer.id];
    this.filtersData['channelIds'] = uniqueChannels.map(c => c.id);
    this.filtersData['screenIds'] = uniqueScreens.map(s => s.id);
    this.filtersData['placement'] = this.placements.map(p => p.id);
    this.filtersData['bookingNumbers'] = engagementsArray.map(e => e.id);
    this.setFiltersValue();

    this.loadScreenOrGroups('', 0, undefined, () => this.getMediaFiles(this.filtersData['bookingNumbers']));
  }

  setFiltersValue(): void {
    this.statisticsService.form.get('customerIds').setValue(this.filtersData.customerId);
    this.statisticsService.form.get('channelIds').setValue(this.filtersData.channelIds);
    this.statisticsService.form.get('screenIds').setValue(this.filtersData.screenIds);
    this.statisticsService.form.get('placement').setValue(this.placements.map(p => p.id));
    this.statisticsService.form.get('bookingNumbers').setValue(this.filtersData.bookingNumbers);
    this.statisticsService.form.get('timeframe').setValue(TimeframeEnum.Custom);
    this.statisticsService.form.get('start').setValue(this.preselectedStartDate);
    this.statisticsService.form.get('end').setValue(this.preselectedEndDate);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fetchedData && changes.fetchedData.previousValue && changes.fetchedData.currentValue) {
      this.fillInputData('bookingNumbers', changes.fetchedData.currentValue);
    }
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  getChannels(customerIds?: number[], preselectChannel?: boolean) {
    if (this.preSelectedFilters) {
      this.statisticsService.form.get('channelIds').setValue(this.filtersData.channelIds);
    }

    let callApi = customerIds && customerIds.length > 0 ? this.statisticsApi.getChannelForStatistics(customerIds) : of(null);
    callApi.pipe(takeUntil(this.destroy$)).subscribe((channelsResponse: IdName[]) => {
      this.channelResponse = channelsResponse;
      this.fillInputData('channelIds', this.channelResponse || []);
      if (this.preSelectedFilters) {
        this.statisticsService.form.get('bookingNumbers').setValue(this.filtersData.bookingNumbers);
      }

      if (this.activeReport === ActiveReportEnum.Reach || this.activeReport === ActiveReportEnum.Exposures) {
        if (preselectChannel) {
          const channelIdArray = this.channelResponse.map(c => c.id);
          this.statisticsService.form.get('channelIds').setValue(channelIdArray);
          this.getPlacements();
        }
      } else {
        const channelWithoutDeletedItems = channelsResponse?.filter(ch => ch.id !== -1) || [];
        if (channelWithoutDeletedItems.length === 1 && preselectChannel) {
          this.statisticsService.form.get('channelIds').setValue([channelWithoutDeletedItems[0].id]);
          this.getPlacements();
        }
      }
    });
  }

  private resetScreenFilterAndReload(): void {
    this.screenFilter.clear();
    const config = this.filterInputs.get('screenIds');
    if (config) {
      config.comboboxOptions = [];
      config.comboboxParams = { ...this.screenFilter.params };
    }
    this.loadScreenOrGroups();
  }

  loadScreenOrGroups(search = '', pageNumber = 0, returnAllResults?: boolean, onLoaded?: () => void): void {
    const channelIds: number[] = this.statisticsService.form.get('channelIds').value || [];
    const placementIds: number[] = this.statisticsService.form.get('placement').value || [];
    const selectedScreenIds: number[] = this.statisticsService.form.get('screenIds').value || [];
    const params: ActiveScreenOrGroupParameters = {
      channelIds,
      placementIds,
      deviceTypeIds: this.setDeviceTypeFiltersForScreens(),
      ...([ActiveReportEnum.PeopleCounter, ActiveReportEnum.POI].includes(this.activeReport)
        ? { trackingTypeIds: this.statisticsService.form.get('tracking').value || [] }
        : {}),
      text: search,
      pageNumber,
      pageSize: this.screenFilter.params.pageSize,
      includeInactive: selectedScreenIds.includes(-1),
      returnAllResults,
    };

    const shouldAutoSelect = pageNumber === 0 && this.activeReport !== ActiveReportEnum.Reach;

    this.statisticsApi
      .getScreensAndGroups(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.screenFilter.handleResponse(response, pageNumber);
        const config = this.filterInputs.get('screenIds');
        if (config) {
          config.comboboxOptions = this.screenFilter.options;
          config.comboboxParams = { ...this.screenFilter.params };
        }

        if (pageNumber === 0) {
          if (this.preSelectedFilters) {
            const selectedScreens = this.screenFilter.options.filter(o =>
              (this.filtersData.screenIds || []).includes(o.id as number)
            );
            this.toggleSelectDeselect(true, 'screenIds', selectedScreens);
          } else if (shouldAutoSelect) {
            const screenIds = this.screenFilter.options.map(o => o.id as number);
            this.statisticsService.form.get('screenIds')?.setValue(screenIds, { emitEvent: false });

            if (this.activeReport === ActiveReportEnum.Exposures) {
              this.getEngagements();
            }
          }
        }

        onLoaded?.();
      });
  }

  getSelectedScreenIds(): number[] {
    return this.screenFilter.getSelectedIds(
      this.statisticsService.form.get('screenIds').value || []
    );
  }

  onScreenSearch = (term: string): void => {
    this.lastScreenSearch = term;
    this.loadScreenOrGroups(term, 0);
  };

  onScreenLoadMore = (params: { pageNumber: number; returnAllResults?: boolean }): void => {
    this.loadScreenOrGroups(this.lastScreenSearch, params.pageNumber, params.returnAllResults);
  };

  getPlacements() {
    this.placements = [];
    if (
      this.statisticsService.form.get('customerIds').value.length === 0 ||
      this.statisticsService.form.get('channelIds').value.length === 0
    ) {
      this.fillInputData('placement', []);
      return;
    }

    let channelIds: number[] = [];

    if (this.statisticsService.form.get('channelIds').value && this.statisticsService.form.get('channelIds').value.length > 0) {
      channelIds = this.statisticsService.form.get('channelIds').value;
    }

    this.statisticsApi
      .getPlacements(channelIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: placements => {
          this.placements = placements;

          const placementsArray = this.placements.length > 0 ? this.placements : [];
          this.fillInputData('placement', placementsArray);

          const placementsIds = this.placements.map(p => p.id);
          this.statisticsService.form.get('placement').setValue(placementsIds);

          this.resetScreenFilterAndReload();
        },
        error: () => {
          console.log('error');
        },
      });
  }

  engagementsRequest(params?): Observable<any> {
    let isTimeFrameNull = this.statisticsService.form.get('timeframe').value == null;

    let body: EngagementRequest = {
      customerIds: this.statisticsService.form.get('customerIds').value || [],
      startDate: this.statisticsService.form.get('start').value,
      endDate: this.statisticsService.form.get('end').value,
      channelIds: [],
      screenIds: this.statisticsService.form.get('screenIds').value || [],
      clientIds: this.statisticsService.form.get('clientIds')?.value || [],
      types: this.statisticsService.form.get('bookingTypes')?.value || [],
      placements: this.placements.map(p => {
        return p.id;
      }),
      ...(params && { ...params }),
    };

    const timeFrame = this.getTimeFrame();

    if (timeFrame) {
      body.startDate = timeFrame[0];
      body.endDate = timeFrame[1];
    }

    return isTimeFrameNull ? of(null) : this.statisticsApi.getStatisticsEngagements(body).pipe(takeUntil(this.destroy$));
  }

  getEngagements() {
    this.engagementsRequest()
      .pipe(filter(res => !!res))
      .subscribe(engagements => {
        this.engagements = engagements;
        this.filteredEngagements = engagements;
        this.fillInputData('bookingNumbers', engagements);
        const engagementItems = engagements.items ? engagements.items : engagements;
        this.getMediaFiles(engagementItems.map(e => e.id));
      });
  }

  getMediaFiles(engagementIds: number[], selectMedias?: boolean) {
    if (!Array.isArray(engagementIds) || engagementIds.length === 0) {
      return;
    }
    this.statisticsApi
      .getEngagementMediaFiles({ engagementIds })
      .pipe(takeUntil(this.destroy$))
      .subscribe(medias => {
        this.medias = medias;
        this.filteredMedias = medias;
        this.fillInputData('mediaFiles', medias);

        if (this.preSelectedFilters || selectMedias) {
          this.toggleSelectDeselect(true, 'mediaFiles', medias);

          if (this.preSelectedFilters) {
            this.onCreateReport();
          }
        }
      });
  }

  getClients(customerIds: number[]) {
    this.clientApi.getClientOptions(customerIds).subscribe(clients => {
      this.clients = clients;
      this.fillInputData('clientIds', clients || []);
    });
  }

  getCategories(customerIds: number[]) {
    this.toggleSelectDeselect(false, 'poiCategoryName', []);
    if (customerIds.length == 0) return;
    this.statisticsApi
      .getStatisticsCategories({ customerIds })
      .pipe(takeUntil(this.destroy$))
      .subscribe(categories => {
        this.categories = categories;
        this.fillInputData('poiCategoryName', categories);
        this.statisticsService.form.get('poiCategoryName')?.setValue(this.categories.map(c => c.id));
        this.getPOI(customerIds);
      });
  }

  getPOI(customerIds: number[]) {
    this.toggleSelectDeselect(false, 'poiName', []);
    if (customerIds.length == 0 || this.statisticsService.form.get('poiCategoryName').value.length == 0) return;

    this.statisticsApi
      .getStatisticsPersonsOfInterest({ customerIds })
      .pipe(takeUntil(this.destroy$))
      .subscribe(pois => {
        this.pois = pois;
        this.filteredPOIs = [{ id: -1, name: 'reports.form.includeDeletedPOI' }].concat(this.pois);
        this.fillInputData('poiName', this.filteredPOIs);
        this.statisticsService.form.get('poiName')?.setValue(this.pois.map(c => c.id));
        this.onCreateReport();
      });
  }

  reportShown(data) {
    if (this.activeReport == ActiveReportEnum.Exposures) {
      this.preSelectedFilters = false;
    }
  }

  checkTimeFrame() {
    return this.statisticsService.form.get('timeframe')?.value === TimeframeEnum.Custom;
  }

  getTimeFrame(): [string, string] {
    const form = this.statisticsService.form;
    const timeframe = form.get('timeframe')?.value;
    const startDateValue = form.get('start')?.value;
    const endDateValue = form.get('end')?.value;

    if (timeframe === TimeframeEnum.Custom) {
      if (this.activeReport === ActiveReportEnum.Exposures && !startDateValue && !endDateValue) {
        const [startDate, endDate] = getLastCalendarYear();
        form.get('start')?.setValue(startDate);
        form.get('end')?.setValue(endDate);
        return [startDate, endDate];
      }
      return null;
    }

    const timeframeFunctionMap = getTimeFrameFunctionMap();
    const dateRangeFn = timeframeFunctionMap[timeframe];

    return dateRangeFn ? dateRangeFn() : null;
  }

  generateBody(reachReportType?: ReachReportTypeEnum) {
    let body = {
      ...this.params,
      ...this.statisticsService.form.value,
      customerIds: this.statisticsService.form.get('customerIds').value || [this.customerId],
    };
    if (this.statisticsService.form.get('timeframe').value === TimeframeEnum.Custom) {
      const startValue = this.statisticsService.form.get('start').value;
      const endValue = this.statisticsService.form.get('end').value;
      body.startDate = startValue instanceof DateTime ? startValue.toISODate() : startValue;
      body.endDate = endValue instanceof DateTime ? endValue.toISODate() : endValue;
    }

    if (this.statisticsService.form.get('timeframe').value !== TimeframeEnum.Custom) {
      const timeframeFunctionMap = getTimeFrameFunctionMap();
      const timeframe = this.statisticsService.form.get('timeframe')?.value;
      const dateRangeFn = timeframeFunctionMap[timeframe];

      if (dateRangeFn) {
        const [startDate, endDate] = dateRangeFn();
        body.startDate = startDate;
        body.endDate = endDate;
      }
    }

    if (this.title === 'reports.titleReachReports') {
      body.IncludeTrackingType = false;
    }

    if (this.title === 'reports.titlePeopleCounter') {
      body.IncludeTrackingType = true;
    }

    if (this.activeReport === ActiveReportEnum.Reach) {
      body.reachReportType = reachReportType || ReachReportTypeEnum.ReachPeople;
    }

    body.placements = this.statisticsService.form.get('placement').value;
    const { created, timeframe, placement, ...filteredBody } = body;

    if ([ActiveReportEnum.Reach, ActiveReportEnum.PeopleCounter, ActiveReportEnum.POI, ActiveReportEnum.Exposures].includes(this.activeReport)) {
      filteredBody.screenIds = this.getSelectedScreenIds();
    }

    return filteredBody;
  }

  onDownloadReport() {
    if (!this.statisticsService.form.valid) return;

    const currentLocalDate = DateTime.local().toISODate();
    const body = this.generateBody();
    body.includePagination = false;
    body.currentLocalDate = currentLocalDate;

    this.setSecondarySortBy(body);

    const callFunction = this.statisticsApi[`download${ActiveReportEnum[this.activeReport]}Reports`](body);

    callFunction.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      if (data) {
        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const a = document.createElement('a');
        const today = DateTime.local();
        const formattedDate = today.toISODate();
        a.href = window.URL.createObjectURL(blob);
        let text;
        if (this.activeReport === ActiveReportEnum.Exposures) text = 'BizLab Flow Campaign Report';
        if (this.activeReport === ActiveReportEnum.PeopleCounter) text = 'BizLab Flow People Counter Report';
        if (this.activeReport === ActiveReportEnum.Reach) text = 'BizLab Flow Reach Report';
        if (this.activeReport === ActiveReportEnum.POI) text = 'BizLab Flow POI Report';
        a.download = `${text} ${formattedDate}`;
        a.click();
      }
    });
  }
  onCreateReport(reachReportType?: ReachReportTypeEnum) {
    if (!this.statisticsService.form.valid) return;
    const currentFilters = this.statisticsService.form.getRawValue();
    const filtersChanged = JSON.stringify(currentFilters) !== JSON.stringify(this.lastUsedFilters);

    // Only reset reports if filters changed
    if (filtersChanged) {
      this.peopleReport = null;
      this.vehicleReport = null;
      this.lastUsedFilters = currentFilters;
    }

    const body = this.generateBody(reachReportType);

    this.setSecondarySortBy(body);

    const callFunction = this.statisticsApi[`get${ActiveReportEnum[this.activeReport]}Reports`](body);

    callFunction.subscribe(reports => {
      if (this.activeReport !== ActiveReportEnum.Reach) {
        this.reports = reports;
      } else {
        if (reachReportType && reachReportType === ReachReportTypeEnum.ReachVehicles) {
          this.vehicleReport = reports;
          this.vehicleReportCount = reports.totalReportCount;
        } else {
          this.peopleReport = reports;
          this.peopleReportCount = reports.totalReportCount;
          this.selectedIndex = 0;
        }
      }
      this.includeVehicleDataReach = reports.includeVehicleData || false;
      this.includeAdminDetailsPOI = body.includePOIAdministrativeDetails;
      this.includeSmsDetailsPOI = body.includeSMSNotifications;
      this.includeAvgTimeInChannel = body.includeAverageTimeInChannel;
      this.showDetailedReport = body.showDetailedReport ?? true;
      this.isDownloadEnabled = true;
    });
  }

  fetchNextBookings() {
    let params: EngagementRequest = {
      pageNumber: this.engagements.currentPage + 1,
    };

    this.engagementsRequest(params)
      .pipe(filter(res => !!res))
      .subscribe(engagements => {
        if (this.finalEngagements.items.length) {
          this.finalEngagements = { ...this.finalEngagements, items: [...this.finalEngagements.items, ...engagements.items] };
        } else {
          this.finalEngagements = { ...this.engagements, items: [...this.engagements.items, ...engagements.items] };
        }

        this.engagements = engagements;
        this.fillInputData('bookingNumbers', this.finalEngagements);

        this.isFilterDataReady = true;
      });
  }

  searchBookings(value: string) {
    const params = {
      pageNumber: 0,
      text: value,
    };

    this.engagementsRequest(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe(engagements => {
        this.finalEngagements = engagements;
        this.engagements = engagements;

        this.fillInputData('bookingNumbers', this.finalEngagements);

        this.isFilterDataReady = true;
      });
  }

  setSecondarySortBy(body: any) {
    const secondarySortBy = this.activeReport === ActiveReportEnum.Exposures ? 'MediaFile' : 'ScreenName';
    body.secondarySortBy = secondarySortBy;
  }

  getNewReport(event) {
    this.params = event;
    this.onCreateReport(event.reachReportType);
  }

  translateMonth(month: string) {
    if (month.length > 0) return `${this.translateConfigService.instant(MonthTranslations[month])}`;
    else return '';
  }

  toggleSelectDeselect(selectAll: boolean, formControlName: string, array: any): void {
    if (this.activeReport === ActiveReportEnum.Exposures) {
      const value = selectAll ? array.map((a: any) => a.id) : [];
      this.statisticsService.form.get(formControlName).setValue(value, { emitEvent: false });
    }
  }

  stopEventPropagation(event: Event): void {
    event.stopPropagation();
  }

  // Statistics form START
  private initForm(): void {
    let filterArray: string[] = ['timeframe', 'start', 'end', 'customerIds'];

    const filterMapping = {
      [ActiveReportEnum.Reach]: filterConfig.reach,
      [ActiveReportEnum.Exposures]: filterConfig.exposures,
      [ActiveReportEnum.PeopleCounter]: [...filterConfig.peopleCounter, 'includeAverageTimeInChannel', 'showDetailedReport'],      
      [ActiveReportEnum.POI]: filterConfig.poi,
    };

    const filters = filterMapping[this.activeReport];
    filterArray = [...filterArray, ...filters];

    filterArray.forEach(item => {
      const filter = reportsFormConfig(item, this.activeReport);
      if (item === 'screenIds' && [ActiveReportEnum.Reach, ActiveReportEnum.PeopleCounter, ActiveReportEnum.POI, ActiveReportEnum.Exposures].includes(this.activeReport)) {
        filter.onSearch = this.onScreenSearch;
        filter.onLoadMore = this.onScreenLoadMore;
      }
      this.filterInputs.set(item, filter);
    });

    this.fillInputData('timeframe', this.timeframeTypes);
    this.fillInputData('tracking', this.trackingTypes);
    this.fillInputData('bookingTypes', this.engagementTypes);
    this.fillInputData('placement', []);
  }

  fillInputData(formControlName: string, data: any) {
    let item = this.filterInputs.get(formControlName);
    if (item) {
      const responseData = data?.items ? data?.items : data;
      const transformedArray: any[] = (responseData || []).map(item => {
        const { id, name, presentationId } = item;
        return {
          id: formControlName == 'bookingTypes' ? EngagementTypeEnum[id] : id,
          name: presentationId
            ? `<div><strong>${presentationId}</strong>: ${name}</div>`
            : (name?.split('-')[1] && formControlName == 'timeframe'
              ? `${name?.split('-')[0]}-${this.translateMonth(name?.split('-')[1])}`
              : name) || '',
        };
      });
      item.data = transformedArray;
      item.filteredData = transformedArray;
    }
  }

  // Construct an array of fields that need to be reset when a filter is changed
  resetFlow(formControlName: string) {
    const state = {
      customerIds: () => {
        const fields = ['screenIds', 'channelIds', 'placement'];

        if (this.activeReport === ActiveReportEnum.Exposures) {
          fields.push('mediaFiles', 'bookingNumbers');
        }

        if (this.activeReport === ActiveReportEnum.POI) {
          fields.push('poiCategoryName', 'poiName');
        }

        return fields;
      },
      channelIds: () => {
        const fields = ['screenIds'];

        if (this.activeReport === ActiveReportEnum.Exposures) {
          fields.push('mediaFiles', 'bookingNumbers');
        }
        return fields;
      },
      placement: () => {
        const fields = [];

        if (this.activeReport === ActiveReportEnum.Exposures) {
          fields.push('mediaFiles', 'bookingNumbers');
        }
        return fields;
      },
      screenIds: () => {
        const fields = [];

        if (this.activeReport === ActiveReportEnum.Exposures) {
          fields.push('mediaFiles', 'bookingNumbers');
        }
        return fields;
      },
      poiCategoryName: () => {
        const fields = [];
        if (this.activeReport === ActiveReportEnum.POI) {
          fields.push('poiName');
        }
        return fields;
      },
      timeframe: () => { },
      tracking: () => { },
      end: () => { },
      start: () => { },
      bookingNumbers: () => { },
    };

    return state[formControlName]();
  }

  // Reset the form fields to their default values
  resetFilterFormFields(property: string) {
    const fields = this.resetFlow(property);

    fields.forEach(field => {
      this.statisticsService.form.get(field).setValue([]);
      this.fillInputData(field, []);
    });
  }

  onFilterChanged = (values: { filter: number[] | string[] | number | string; type: string; form: FormGroup; allFilters: any }): void => {
    this.isDownloadEnabled = false;

    switch (values.type) {
      case 'customerIds':
        {
          this.resetFilterFormFields('customerIds');
          this.getChannels(values.allFilters.customerIds, true);
          this.activeReport === ActiveReportEnum.Exposures && this.getClients(values.allFilters.customerIds);
          this.activeReport === ActiveReportEnum.POI && this.getCategories(values.allFilters.customerIds);
        }
        break;
      case 'channelIds':
        {
          this.resetFilterFormFields('channelIds');
          this.getPlacements();
          if (this.activeReport === ActiveReportEnum.Reach || this.activeReport === ActiveReportEnum.Exposures) {
            const channelId = values.allFilters.channelIds?.[0];
            const customerIds = [...(values.allFilters.customerIds || [])];

            if (channelId === -1) {
              customerIds.push(-1);
            }

            if (this.isAdmin) {
              this.getChannels(customerIds);
            } else {
              this.toggleChannelsForNonAdminRole(values.allFilters.channelIds);
            }
          }
        }
        break;
      case 'placement':
        {
          this.resetFilterFormFields('placement');
          this.resetScreenFilterAndReload();
        }
        break;
      case 'screenIds':
        {
          this.resetFilterFormFields('screenIds');
          this.activeReport === ActiveReportEnum.Exposures && this.getEngagements();
        }
        break;
      case 'poiCategoryName':
        {
          this.resetFilterFormFields('poiCategoryName');
          this.getPOI(this.statisticsService.form.get('customerIds').value);
        }
        break;
      case 'timeframe':
        this.changeTimeframe(values.filter as number);
        break;
      case 'tracking':
        if ([ActiveReportEnum.PeopleCounter, ActiveReportEnum.POI].includes(this.activeReport)) {
          this.resetScreenFilterAndReload();
        }
        break;
      case 'end':
        this.activeReport === ActiveReportEnum.Exposures && this.getEngagements();
        break;
      case 'start':
        this.activeReport === ActiveReportEnum.Exposures && this.getEngagements();
        break;
      case 'bookingNumbers':
        this.activeReport === ActiveReportEnum.Exposures && this.getMediaFiles(values.allFilters.bookingNumbers, true);
        break;
    }
  };

  changeTimeframe(value: number) {
    const formStartDate = this.statisticsService.form.get('start');
    const formEndDate = this.statisticsService.form.get('end');
    if (value === TimeframeEnum.Custom) {
      formStartDate.setValidators(Validators.required);
      formEndDate.setValidators(Validators.required);

      formStartDate?.setValue(DateTime.now().startOf('month').toISODate());
      formEndDate?.setValue(DateTime.now().endOf('month').toISODate());

      this.startDate = undefined;
      this.endDate = undefined;
    } else {
      formStartDate.removeValidators([Validators.required]);
      formEndDate.removeValidators([Validators.required]);
      formStartDate.setValue(null);
      formEndDate.setValue(null);

      const timeframeFunctionMap = getTimeFrameFunctionMap();
      const timeframe = this.statisticsService.form.get('timeframe')?.value;
      const dateRangeFn = timeframeFunctionMap[timeframe];

      if (dateRangeFn) {
        const [startDate, endDate] = dateRangeFn();
        this.startDate = startDate;
        this.endDate = endDate;
      }
    }

    formStartDate?.updateValueAndValidity();
    formEndDate?.updateValueAndValidity();

    if (this.activeReport === ActiveReportEnum.Exposures) {
      this.getEngagements();
    }
  }

  ngOnDestroy(): void {
    this.statisticsService.form = this.formBuilder.group({});
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setDeviceTypeFiltersForScreens(): number[] {
    const deviceTypes: number[] = [DeviceType.ScreenWithSensor];

    const includeSensors = this.activeReport === ActiveReportEnum.PeopleCounter || this.activeReport === ActiveReportEnum.POI;
    const includeScreensOnly = this.activeReport === ActiveReportEnum.Exposures;
    if (includeSensors) {
      deviceTypes.push(DeviceType.SensorOnly);
    }
    if (includeScreensOnly) {
      deviceTypes.push(DeviceType.ScreenOnly);
    }
    return deviceTypes;
  }

  selectedTabChange(index: number) {
    this.selectedIndex = index;
    if (this.activeReport !== ActiveReportEnum.Reach) return;

    if (this.selectedIndex === 0 && !this.peopleReport) {
      this.params.sortBy = 'Date';
      this.onCreateReport(ReachReportTypeEnum.ReachPeople);
    }

    if (this.selectedIndex === 1 && !this.vehicleReport) {
      this.params.sortBy = 'Date';
      this.onCreateReport(ReachReportTypeEnum.ReachVehicles);
    }
  }

  get showNoData(): boolean {
    if (this.activeReport !== ActiveReportEnum.Reach) {
      return !this.reports;
    }

    if (this.selectedIndex === 0) {
      return !this.peopleReport;
    }

    if (this.selectedIndex === 1) {
      return !this.vehicleReport;
    }

    return true;
  }

  get hideDownloadButton(): boolean {
    if (!this.statisticsService.form.valid || !this.isDownloadEnabled) {
      return true;
    }

    if (this.activeReport !== ActiveReportEnum.Reach) {
      return (
        !this.reports ||
        this.reports.totalVisitors === 0 ||
        this.reports.total === 0 ||
        this.reports?.personOfInterestStatisticsReports?.length === 0
      );
    }

    const hasPeopleData = !!this.peopleReport?.reachDates?.length;

    const hasVehicleData = !!this.vehicleReport?.reachDates?.length;

    return !(hasPeopleData || hasVehicleData);
  }
}
